import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Format a Date using local time to avoid UTC offset shifting the day
const toLocalDateStr = (d) => {
    if (!d) return null;
    const y  = d.getFullYear();
    const m  = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
};

// The API returns UTC timestamps without a zone suffix, so mark them as UTC before converting to local time.
const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const dateEq =(a, b) =>
    (a == null && b == null) || (a != null && b != null && a.getTime() === b.getTime());

export default function VinViewDashboardPage() {
    const toast = useRef(null);
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [calls, setCalls] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ clients: [], clientReferences: [] });

    useEffect(() => { document.title = 'VIN View Dashboard'; }, []);

    const defaultFrom = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setHours(0,0,0,0); return d; })();
    const defaultTo   = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

    const [filters, setFilters] = useState({
        client: null,
        clientReference: null,
        startedFrom: defaultFrom,
        startedTo: defaultTo,
    });
    const [appliedFilters, setAppliedFilters] = useState(filters);

    const fetchData = useCallback(async (activeFilters) => {
        setLoading(true);
        try {
            const token = await fetchAccessToken(getAccessTokenSilently);
            const params = new URLSearchParams();
            if (activeFilters.client) params.append('client', activeFilters.client);
            if (activeFilters.clientReference) params.append('clientReference', activeFilters.clientReference);
            if (activeFilters.startedFrom) params.append('startedFrom', toLocalDateStr(activeFilters.startedFrom));
            if (activeFilters.startedTo) params.append('startedTo', toLocalDateStr(activeFilters.startedTo));

            const response = await fetch(
                `${API_BASE_URL}/Dashboard/decoder-stats?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) await apiCallErrorHandler(response, 'Failed to fetch decoder stats');
            setRows(await response.json());

            // Individual calls are shown only when a single client + reference is selected
            if (activeFilters.client && activeFilters.clientReference) {
                const callsResponse = await fetch(
                    `${API_BASE_URL}/Dashboard/decoder-calls?${params}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!callsResponse.ok) await apiCallErrorHandler(callsResponse, 'Failed to fetch decoder calls');
                setCalls(await callsResponse.json());
            } else {
                setCalls([]);
            }
        } catch (ex) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: ex.message });
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const token = await fetchAccessToken(getAccessTokenSilently);
            const response = await fetch(
                `${API_BASE_URL}/Dashboard/decoder-filters`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) await apiCallErrorHandler(response, 'Failed to fetch filter options');
            setFilterOptions(await response.json());
        } catch (ex) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: ex.message });
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        fetchFilterOptions();
        fetchData(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtersAreDirty = () =>
        filters.client !== appliedFilters.client ||
        filters.clientReference !== appliedFilters.clientReference ||
        !dateEq(filters.startedFrom, appliedFilters.startedFrom) ||
        !dateEq(filters.startedTo, appliedFilters.startedTo);

    const handleApply = () => {
        setAppliedFilters(filters);
        fetchData(filters);
    };

    const toOptions = (values) => values.map((v) => ({ label: v, value: v }));

    const totals = rows.reduce(
        (t, r) => ({
            total: t.total + r.total,
            unique: t.unique + r.unique,
            succeeded: t.succeeded + r.succeeded,
            unsucceeded: t.unsucceeded + r.unsucceeded,
        }),
        { total: 0, unique: 0, succeeded: 0, unsucceeded: 0 }
    );

    const footer = (
        <div className="flex gap-4">
            <span><strong>Total:</strong> {totals.total.toLocaleString()}</span>
            <span><strong>Unique:</strong> {totals.unique.toLocaleString()}</span>
            <span><strong>Succeeded:</strong> {totals.succeeded.toLocaleString()}</span>
            <span><strong>Unsucceeded:</strong> {totals.unsucceeded.toLocaleString()}</span>
        </div>
    );

    return (
        <div className="p-4">
            <Toast ref={toast} />
            <h2 className="mb-3">VIN View Dashboard</h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 align-items-end">
                <div className="flex flex-column gap-1">
                    <label htmlFor="fromDate">From</label>
                    <Calendar
                        inputId="fromDate"
                        value={filters.startedFrom}
                        onChange={(e) => setFilters((f) => ({ ...f, startedFrom: e.value }))}
                        dateFormat="dd.mm.yy"
                        showIcon
                        showButtonBar
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="toDate">To</label>
                    <Calendar
                        inputId="toDate"
                        value={filters.startedTo}
                        onChange={(e) => setFilters((f) => ({ ...f, startedTo: e.value }))}
                        dateFormat="dd.mm.yy"
                        showIcon
                        showButtonBar
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="clientFilter">Client</label>
                    <Dropdown
                        inputId="clientFilter"
                        value={filters.client}
                        options={toOptions(filterOptions.clients)}
                        onChange={(e) => setFilters((f) => ({ ...f, client: e.value }))}
                        placeholder="All clients"
                        showClear
                        filter
                        scrollHeight="400px"
                        style={{ minWidth: '10rem' }}
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="clientRefFilter">Client reference</label>
                    <Dropdown
                        inputId="clientRefFilter"
                        value={filters.clientReference}
                        options={toOptions(filterOptions.clientReferences)}
                        onChange={(e) => setFilters((f) => ({ ...f, clientReference: e.value }))}
                        placeholder="All references"
                        showClear
                        filter
                        scrollHeight="400px"
                        style={{ minWidth: '12rem' }}
                        disabled={loading}
                    />
                </div>
                <Button
                    label="Apply"
                    icon="pi pi-search"
                    onClick={handleApply}
                    disabled={loading}
                />
            </div>

            {filtersAreDirty() && (
                <div className="flex align-items-center gap-2 mb-3 px-3 py-2 border-round"
                    style={{ background: '#fff8e1', border: '1px solid #f9a825', color: '#7b5800' }}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '1rem' }} />
                    <span>Filters have changed since the last call. Click Apply to reflect the current selection.</span>
                </div>
            )}

            <DataTable
                value={rows}
                loading={loading}
                footer={rows.length > 0 ? footer : null}
                emptyMessage="No data found. Adjust the filters and click Apply."
                stripedRows
                size="small"
            >
                {!appliedFilters.client && <Column field="optiClient" header="Client" sortable />}
                {!appliedFilters.clientReference && <Column field="clientReference" header="Client reference" sortable />}
                <Column field="total" header="Total requests" sortable />
                <Column field="unique" header="Unique requests" sortable />
                <Column field="succeeded" header="Succeeded" sortable />
                <Column field="unsucceeded" header="Unsucceeded" sortable />
            </DataTable>

            {appliedFilters.client && appliedFilters.clientReference && (
                <>
                    <h3 className="mt-5 mb-3">Individual calls</h3>
                    <DataTable
                        value={calls}
                        loading={loading}
                        paginator
                        rows={50}
                        rowsPerPageOptions={[50, 100, 200]}
                        emptyMessage="No calls found."
                        stripedRows
                        size="small"
                    >
                        <Column header="Date/time" body={(r) => formatDateTime(r.createdUtc)} />
                        <Column field="vin" header="VIN" />
                        <Column field="apiVersion" header="API version" />
                        <Column header="Succeeded" body={(r) => (r.isSucceeded ? 'Yes' : 'No')} />
                        <Column header="Duplicate" body={(r) => (r.isDuplicate ? 'Yes' : 'No')} />
                    </DataTable>
                    {calls.length >= 5000 && (
                        <p className="text-color-secondary mt-2">Showing the latest 5,000 calls only. Narrow the date range to see older ones.</p>
                    )}
                </>
            )}
        </div>
    );
}
